import type { PinState } from '$lib/stores/block-state'
import type createCustomMarkerClass from '$lib/map/CustomMarker'
import Marker from '$lib/components/maps/Marker.svelte'
import type { MapViewBlockFieldsFragment } from '$lib/queries/fragments/generated/MapViewBlockFields'
import { getCustomColorHexCode } from '$lib/utils/custom-colors'
import blockState from '$lib/stores/block-state'
import { mount, unmount } from 'svelte'
import { BlockType, CustomColor, CustomIcon } from '../graphql/enums'
import { mapStore } from '../stores/map-state'

export type CustomMarkerType = InstanceType<ReturnType<typeof createCustomMarkerClass>>

export interface CustomMarkerItem {
	customMarker: CustomMarkerType
	blockId?: string
	component?: any
	isSearchMarker: boolean
	pinState: PinState | null
}

export function createMarkerComponent(
	pinState: PinState | null,
	isSearchMode: boolean,
	handleInteraction: (interactionType: string, blockId: string | null, isSearch: boolean) => void,
) {
	if (!pinState) return null

	const markerElement = document.createElement('div')
	const props = {
		blockId: pinState.id,
		icon: pinState.icon || CustomIcon.Default,
		color: pinState?.color || CustomColor.Default,
		blockType: pinState?.blockType || BlockType.Place,
		isActive: pinState?.isActive,
		isHighlighted: pinState?.isHighlighted,
		shouldHighlight: pinState?.shouldHighlight,
		isSearchMode: isSearchMode,
		// Pass the interaction handler directly as a prop
		interact: (blockId: string, interactionType: string | null, isSearch: boolean) => {
			handleInteraction(interactionType, blockId, isSearch)
		},
		featuredImage: pinState?.featuredImage,
	}

	const markerComponent = mount(Marker, {
		target: markerElement,
		props,
	})

	return {
		markerElement,
		markerComponent,
	}
}

export async function removePins(pins: CustomMarkerItem[]): Promise<void> {
	if (!window.google) return

	for (const pin of pins) {
		try {
			// Remove from Google Maps first - this triggers onRemove()
			if (pin?.customMarker) {
				pin.customMarker.setMap(null)
			}

			// Now try to clean up the component
			if (pin?.component) {
				try {
					// Only attempt to unmount if it looks like a valid component
					// Check for the error proxy state we saw earlier
					const isErrorProxy =
						typeof pin.component === 'object' &&
						pin.component !== null &&
						pin.component.$destroy &&
						pin.component.$destroy.toString().includes('error')

					if (!isErrorProxy) {
						unmount(pin.component)
					}
				} catch (error) {
					// Silent catch - component was likely already unmounted
				}
			}

			// Clear references
			if (pin?.customMarker?.div) {
				pin.customMarker.div = null
			}

			if (pin?.customMarker) {
				google.maps.event.clearInstanceListeners(pin.customMarker)
				pin.customMarker = null
			}

			pin.component = null
		} catch (error) {
			console.error(`Failed to remove pin ${pin?.blockId}:`, error)
		}
	}
}

export function updateMarkerZOrder(higherOrderPins: CustomMarkerItem[]): void {
	higherOrderPins.forEach((pin) => {
		if (pin?.customMarker?.div) {
			if (pin?.customMarker?.div) {
				pin.customMarker.div.style.zIndex = '10'
			}
			// Move to end of parent container for additional stacking context
			const parentElement = pin.customMarker.div.parentElement

			if (parentElement) {
				parentElement.removeChild(pin.customMarker.div)
				parentElement.appendChild(pin.customMarker.div)
			}
		}
	})
}

export function createCustomRegionPolygon(
	block: MapViewBlockFieldsFragment,
	map: google.maps.Map,
	handlePolygonClick: (id: string) => void,
	isActive: boolean,
	isHighlighted: boolean,
	shouldHighlight: boolean,
): google.maps.Polygon {
	const customHexColor = getCustomColorHexCode(block?.color, block?.blockType)

	let fillOpacity = 0.08 // default
	if (isHighlighted) {
		fillOpacity = 0.15
	} else if (shouldHighlight) {
		fillOpacity = 0.05
	}

	const regionPolygon = new google.maps.Polygon({
		paths: block?.customRegion?.markers,
		strokeColor: customHexColor,
		strokeOpacity: 0.5,
		strokeWeight: isActive ? 3 : 1.5,
		fillColor: customHexColor,
		fillOpacity,
		zIndex: 1,
	})

	regionPolygon['id'] = block.id

	// Click handler remains the same
	regionPolygon.addListener('click', () => handlePolygonClick(block.id))

	let isMouseInPolygon = false
	// Mouseover still works fine
	regionPolygon.addListener('mouseover', () => {
		blockState.setHoveringPlaceId(block.id)
		isMouseInPolygon = true
	})

	// Add a map-level mousemove listener
	google.maps.event.addListener(map, 'mousemove', (event) => {
		const latLng = event.latLng

		// Check if the mouse is inside the polygon
		const isInside = google?.maps?.geometry?.poly?.containsLocation(latLng, regionPolygon)

		if (isInside && !isMouseInPolygon) {
			// Mouse entered the polygon
			isMouseInPolygon = true
			blockState.setHoveringPlaceId(block.id)
		} else if (!isInside && isMouseInPolygon) {
			// Mouse left the polygon
			isMouseInPolygon = false
			blockState.setHoveringPlaceId(null)
		}
	})

	// Set the polygon on the map
	regionPolygon.setMap(map)
	return regionPolygon
}

export function drawPolygon(
	mapMarkers: { lat: number; lng: number }[],
	map: google.maps.Map,
	existingPolygon: google.maps.Polygon | null = null,
): google.maps.Polygon {
	// If a polygon already exists, set it to null
	if (existingPolygon) {
		existingPolygon.setMap(null)
	}

	// Filter out invalid coordinates
	const validMarkers = mapMarkers.filter(
		(marker) =>
			typeof marker.lat === 'number' &&
			!isNaN(marker.lat) &&
			typeof marker.lng === 'number' &&
			!isNaN(marker.lng),
	)

	const polygon = new google.maps.Polygon({
		paths: validMarkers,
		strokeColor: '#f97316',
		strokeOpacity: 0.5,
		strokeWeight: 1.5,
		fillColor: '#f97316',
		fillOpacity: 0.08,
	})

	polygon.setMap(map)
	return polygon
}

export function clearPolygons(polygons: google.maps.Polygon[]): void {
	polygons?.forEach((polygon) => {
		// Remove the mouseMoveListener if it exists
		// if (polygon['mouseMoveListener']) {
		// 	google.maps.event.removeListener(polygon['mouseMoveListener'])
		// 	polygon['mouseMoveListener'] = null
		// }
		// Remove the polygon from the map
		polygon?.setMap(null)
	})
}

export function updatePolygonHoverState(
	polygon: google.maps.Polygon,
	isHovered: boolean,
	isActive: boolean,
	shouldHighlight: boolean,
): void {
	if (isHovered) {
		polygon?.setOptions({ fillOpacity: 0.25 })
	} else {
		if (isActive) {
			polygon?.setOptions({ fillOpacity: 0.15 })
		} else if (shouldHighlight) {
			polygon?.setOptions({ fillOpacity: 0.05 })
		} else {
			polygon?.setOptions({ fillOpacity: 0.08 })
		}
	}
}

export function isBlockActive(
	block: MapViewBlockFieldsFragment,
	selectedBlockId: string | null,
	parentBlockId: string | null,
	expandedBlockId: string | null,
	isHighlights: boolean,
): boolean {
	if (block?.id === null) return false
	return Boolean(
		block?.id === selectedBlockId ||
			block?.id === parentBlockId ||
			block?.id === expandedBlockId ||
			block?.parentDayId === selectedBlockId ||
			block?.parentId === selectedBlockId ||
			block?.parentListId === selectedBlockId ||
			block?.parentDestination?.id === selectedBlockId ||
			(isHighlights &&
				(block?.parentDayId === parentBlockId ||
					block?.parentId === parentBlockId ||
					block?.parentListId === parentBlockId ||
					block?.parentDestination?.id === parentBlockId)) ||
			(block?.parentDayId !== null &&
				expandedBlockId !== null &&
				block?.parentDayId === expandedBlockId) ||
			(block?.parentId !== null &&
				expandedBlockId !== null &&
				block?.parentId === expandedBlockId) ||
			(block?.parentListId !== null &&
				expandedBlockId !== null &&
				block?.parentListId === expandedBlockId) ||
			(block?.parentDestination?.id !== null &&
				expandedBlockId !== null &&
				block?.parentDestination?.id === expandedBlockId),
	)
}

export function addRegionMarker(
	markers: { lat: number; lng: number }[] | null | undefined,
	location: google.maps.LatLngLiteral,
) {
	if (!markers) markers = []
	if (!isValidLocation(location)) return

	// Check if the clickedLatLng already exists in the markers array
	const alreadyExists = markers.some(
		(marker) => marker.lat === location.lat && marker.lng === location.lng,
	)

	// Only add the new marker if it doesn't exist already
	if (!alreadyExists) {
		blockState.setMapMarkers(markers.concat({ lat: location.lat, lng: location.lng }))
	}
}

export function addPin(location: google.maps.LatLngLiteral) {
	blockState.setMapMarkers([{ lat: location.lat, lng: location.lng }])
}

export function clearMarkers() {
	blockState.setMapMarkers([])
}

function isValidLocation(location: google.maps.LatLngLiteral): boolean {
	return (
		location &&
		typeof location.lat === 'number' &&
		!isNaN(location.lat) &&
		typeof location.lng === 'number' &&
		!isNaN(location.lng)
	)
}

export function setSearchArea(map: google.maps.Map | null) {
	if (!map) {
		blockState.setSearchArea(true)
		return
	}

	const center = map.getCenter()?.toJSON()
	if (center && isValidLocation(center)) {
		mapStore.dispatch({
			type: 'SET_CENTER',
			payload: center,
		})
	}
	blockState.setSearchArea(true)
}
